import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { listAssessmentAttempts, type AssessmentAttempt, type AssessmentType } from '../../lib/apiClient';
import { ASSESSMENT_LABELS, routes } from '../../lib/assessment/routes';
import { FREE_LIMIT, completedCount, remainingAttempts } from '../../lib/assessment/attempts';
import { formatCompletedAt } from '../../lib/report/reportView';

const TYPES: AssessmentType[] = ['bottleneck', 'work_fit', 'perspective'];

type State =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ok'; attempts: AssessmentAttempt[] };

/**
 * What the visitor has completed, and what they have left (U1b).
 *
 * One unfiltered call. `listAssessmentAttempts` takes an optional type, but the
 * three types are wanted together here and three requests to answer one
 * question would be three chances to half-fail.
 *
 * The counts come from the shared `attempts` helpers rather than from arithmetic
 * written here, because `AssessmentRunner` refuses a fourth attempt using the
 * same rule. Two copies is how this section comes to offer an attempt the runner
 * then declines — the visitor is told they have one left, answers fifty
 * questions, and is refused at the end.
 *
 * A failure renders a short line and nothing else. History is the least
 * important of the dashboard's sections, and it must not take the page down
 * with it.
 */
export function AssessmentHistory() {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await listAssessmentAttempts(token);
      if (cancelled) return;
      setState(res.state === 'ok' ? { phase: 'ok', attempts: res.attempts } : { phase: 'error' });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <section className="flex flex-col gap-3" data-testid="assessment-history">
      <h2 className="text-xl font-semibold">Your assessments</h2>

      {state.phase === 'loading' ? (
        <p className="text-muted-foreground">Loading your assessments…</p>
      ) : null}

      {state.phase === 'error' ? (
        <p className="text-muted-foreground" data-testid="assessment-history-error">
          Your assessment history could not be loaded.
        </p>
      ) : null}

      {state.phase === 'ok' ? (
        completedCount(state.attempts, 'bottleneck') +
          completedCount(state.attempts, 'work_fit') +
          completedCount(state.attempts, 'perspective') ===
        0 ? (
          <p className="text-foreground" data-testid="assessment-history-empty">
            You have not completed an assessment yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {TYPES.filter((type) => completedCount(state.attempts, type) > 0).map((type) => {
              const completed = completedCount(state.attempts, type);
              const left = remainingAttempts(state.attempts, type);
              const latest = state.attempts
                .filter((a) => a.assessment_type === type && a.completed_at)
                .map((a) => a.completed_at!)
                .sort()
                .at(-1);

              return (
                <li
                  key={type}
                  className="rounded-lg border border-border p-4"
                  data-testid={`history-${type}`}
                >
                  <div className="font-medium">{ASSESSMENT_LABELS[type]}</div>
                  <div className="text-sm text-muted-foreground">
                    {completed} of {FREE_LIMIT} free attempts used
                    {latest ? ` · last completed ${formatCompletedAt(latest)}` : ''}
                  </div>
                  {left > 0 ? (
                    <Link
                      href={routes.assessment(type)}
                      className="mt-2 inline-block text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      Take it again ({left} left)
                    </Link>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">No free attempts left.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </section>
  );
}
